import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './BlogDetails.css';
import DOMPurify from "dompurify";

const BlogDetails = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogDetails();
  }, [id]);

  const fetchBlogDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/blogs/${id}`);

      if (!response.ok) {
        throw new Error('Blog not found');
      }

      const data = await response.json();
      setBlog(data);

      // Fetch related blogs
      const relatedResponse = await fetch(`${process.env.REACT_APP_API_URL}/blogs`);
      const allBlogs = await relatedResponse.json();
      const related = allBlogs
        .filter(
          (b) => b.id !== parseInt(id) && b.status === 'published' && b.category === data.category
        )
        .slice(0, 3);
      setRelatedBlogs(related);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const shareOnSocial = (platform) => {
    const url = window.location.href;
    const title = blog?.title || '';

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className='blogs'>
        <div className='blogs-container'>
          <div className='responsive-loading'>
            <div className='responsive-spinner' />
            <p>Loading Blogs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='blog-details-error'>
        <div className='error-icon'>❌</div>
        <h2>Blog Not Found</h2>
        <p>{error}</p>
        <Link to='/blogs' className='back-to-blogs-btn'>
          ← Back to Blogs
        </Link>
      </div>
    );
  }

  if (!blog) {
    return null;
  }

  const formatContent = (text) => {
  return text
    .split("\n\n")
    .map(p => `<p>${p}</p>`)
    .join("");
};

  return (
    <div className='blog-details'>
      {/* Breadcrumb */}
      <section className='blog-breadcrumb'>
        <div className='blog-details-container'>
          <nav className='breadcrumb-nav'>
            <Link to='/' className='breadcrumb-link'>
              Home
            </Link>
            <span className='breadcrumb-separator'>›</span>
            <Link to='/blogs' className='breadcrumb-link'>
              Blogs
            </Link>
            <span className='breadcrumb-separator'>›</span>
            <span className='breadcrumb-current'>{blog.title}</span>
          </nav>
        </div>
      </section>

      {/* Hero Section */}
      <section className='blog-hero'>
        <div className='blog-details-container'>
          <div className='blog-hero-content'>
            <div className='blog-meta-header'>
              {blog.category && <span className='blog-category-badge'>{blog.category}</span>}
              <div className='blog-meta-info'>
                <span className='blog-date'>
                  <i className='date-icon'>📅</i>
                  {formatDate(blog.published_at || blog.created_at)}
                </span>
                {blog.Author && (
                  <span className='blog-author'>
                    <i className='author-icon'>👤</i>
                    {blog.Author.name}
                  </span>
                )}
              </div>
            </div>

            <h1 className='blog-title'>{blog.title}</h1>

            {blog.excerpt && <p className='blog-excerpt'>{blog.excerpt}</p>}

            <div className='blog-share'>
              <span className='share-label'>Share:</span>
              <div className='share-buttons'>
                <button onClick={() => shareOnSocial('facebook')} className='share-btn facebook'>
                  <i className='share-icon'>📘</i>
                </button>
                <button onClick={() => shareOnSocial('twitter')} className='share-btn twitter'>
                  <i className='share-icon'>🐦</i>
                </button>
                <button onClick={() => shareOnSocial('linkedin')} className='share-btn linkedin'>
                  <i className='share-icon'>💼</i>
                </button>
                <button onClick={() => shareOnSocial('whatsapp')} className='share-btn whatsapp'>
                  <i className='share-icon'>💬</i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className='blog-content-section'>
        <div className='blog-details-container'>
          <div className='blog-layout'>
            <main className='blog-main-content'>
              {blog.featured_image ? (
                <div className='blog-featured-image'>
                  <img
                    src={
                      blog.featured_image.startsWith('http')
                        ? blog.featured_image
                        : `${process.env.REACT_APP_API_URL_WITH_OUT_API}${blog.featured_image}`
                    }
                    alt={blog.title || 'Blog image'}
                    crossOrigin='anonymous'
                  />
                </div>
              ) : (
                <div className='blog-featured-image'>
                  <div className='blog-image-placeholder'>
                    <i className='placeholder-icon'>📝</i>
                  </div>
                </div>
              )}

            <div
              className="blog-content"
              dangerouslySetInnerHTML={{
                __html: formatContent(blog.content),
              }}
            />
            </main>

            <aside className='blog-sidebar'>
              <div className='sidebar-widget'>
                <h3>About the Author</h3>
                <div className='author-info'>
                  <div className='author-avatar'>
                    <i className='avatar-icon'>👤</i>
                  </div>
                  <div className='author-details'>
                    <h4>{blog.Author?.name || 'Admin'}</h4>
                    <p>
                      Expert education counselor helping students achieve their international
                      education dreams.
                    </p>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related Blogs */}
      {relatedBlogs.length > 0 && (
        <section className='related-blogs'>
          <div className='blog-details-container'>
            <h2>Related Articles</h2>
            <div className='related-blogs-grid'>
              {relatedBlogs.map((relatedBlog) => (
                <article key={relatedBlog.id} className='related-blog-card'>
                  <div className='related-blog-image'>
                    {relatedBlog.featured_image ? (
                      <img
                        src={
                          blog.featured_image.startsWith('http')
                            ? blog.featured_image
                            : `${process.env.REACT_APP_API_URL_WITH_OUT_API}${relatedBlog.featured_image}`
                        }
                        alt={blog.title || 'Blog image'}
                        crossOrigin='anonymous'
                      />
                    ) : (
                      <div className='related-blog-placeholder'>
                        <i className='placeholder-icon'>📝</i>
                      </div>
                    )}
                    {relatedBlog.category && (
                      <span className='blog-category'>{relatedBlog.category}</span>
                    )}
                  </div>
                  <div className='related-blog-content'>
                    <div className='blog-meta'>
                      <span className='blog-date'>
                        <i className='date-icon'>📅</i>
                        {formatDate(relatedBlog.published_at || relatedBlog.created_at)}
                      </span>
                      {relatedBlog.Author && (
                        <span className='blog-author'>
                          <i className='author-icon'>👤</i>
                          {relatedBlog.Author.name}
                        </span>
                      )}
                    </div>
                    <h3>
                      <Link to={`/blog/${relatedBlog.id}`}>{relatedBlog.title}</Link>
                    </h3>
                    <p>
                      {relatedBlog.excerpt?.substring(0, 100) ||
                        relatedBlog.content.replace(/<[^>]*>/g, '').substring(0, 100)}
                      ...
                    </p>
                    <Link to={`/blog/${relatedBlog.id}`} className='read-more-btn'>
                      Read More
                      <i className='arrow-icon'>→</i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetails;
